import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useSwipeable } from 'react-swipeable';
import { motion, AnimatePresence } from 'framer-motion';
import { fivePracticaLocales } from '../../locales/firstScreen';

import { GlobalFonts } from '../../styles/GlobalFonts';
import { Footer } from '../UI/Footer/Footer';

const Container = styled.div`
  background: #151515;
  width: 100%;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: auto;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  height: min(110px, 15vh);
  margin: 45px 0 30px;
  padding: min(13px, 2vh);
  border-radius: 20px;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(/assets/background.gif) center/cover no-repeat;
    mix-blend-mode: normal;
    opacity: 0.9;
    border-radius: 20px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #7354f2;
    opacity: 1;
    mix-blend-mode: multiply;
    border-radius: 20px;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: clamp(40px, 5vw, 34px);
  font-weight: 600;
  margin-bottom: min(8px, 1.2vh);
  margin-top: min(1px, 0.2vh);
  line-height: 100%;
`;

const TheoryButton = styled.button`
  background: none;
  border: 2px solid #ffffff;
  border-radius: 12px;
  padding: 5px 7px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  line-height: 100%;
`;

const Description = styled.p`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  text-align: center;
  margin: 0 22px 10px;
  line-height: 145%;
  letter-spacing: -0.01em;
  white-space: pre-line;
`;

const StakingContainer = styled.div`
  background: #ffffff0d;
  padding: 14px 0 45px;
  width: 100%;
  margin-top: auto;
`;

const Label = styled.span`
  color: #b4b4b4;
  font-size: 12px;
  margin: 0 11px 8px;
  display: block;
`;

const ScrollContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 10px;
  padding-bottom: 100px;
`;

const AmountButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  padding: 12px 21px;
  margin: 0 10px 20px;
  border-radius: 12px;
  border: 2px solid rgba(180, 180, 180, 0.2);
  width: calc(100% - 20px);
  height: 52px;
`;

const AmountInput = styled.input`
  flex: 1;
  background: none;
  border: none;
  color: ${(props) => (Number(props.value) <= 0 ? '#666666' : '#FFFFFF')};
  font-size: 18px;
  font-weight: bold;
  font-family: 'Inter', sans-serif;
  outline: none;
  width: 100%;
  text-align: left;
  padding: 0;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const CurrencyContainer = styled.div`
  display: flex;
  align-items: center;
`;

const CurrencyIcon = styled.img`
  width: 26px;
  height: 26px;
  margin-right: 6px;
`;

const CurrencyText = styled.span`
  color: #ffffff;
  font-size: 18px;
  font-weight: bold;
`;

const AmountText = styled.span`
  flex: 0 0 auto;
  color: #ffffff;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Inter', sans-serif;
  margin-right: auto;
`;

const DurationButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: none;
  padding: 12px 21px;
  margin: 0 10px;
  border-radius: 12px;
  border: 2px solid rgba(180, 180, 180, 0.2);
  width: calc(100% - 20px);
  height: 52px;
  position: relative;
  margin-bottom: 20px;
  cursor: default;
`;

const DurationSliderContainer = styled.div`
  position: absolute;
  bottom: -9px;
  left: 13px;
  right: 13px;
  height: 16px;
  cursor: pointer;
`;

const SliderTrack = styled.div`
  position: absolute;
  height: 4px;
  width: 100%;
  background: rgba(115, 84, 242, 0.2);
  border-radius: 2px;
  bottom: 6px;
`;

const SliderProgress = styled.div<{ progress: number; isDragging: boolean }>`
  position: absolute;
  height: 4px;
  background: #7354f2;
  border-radius: 2px;
  width: ${(props) => props.progress}%;
  bottom: 6px;
  transition: ${(props) => (props.isDragging ? 'none' : 'width 0.3s ease')};
`;

const SliderThumb = styled.div<{ position: number; isDragging: boolean }>`
  position: absolute;
  width: 20px;
  height: 20px;
  background: #7354f2;
  border-radius: 50%;
  bottom: -2px;
  left: ${(props) => props.position}%;
  transform: translateX(-50%);
  cursor: grab;
  transition: ${(props) => (props.isDragging ? 'none' : 'left 0.3s ease')};

  &:active {
    cursor: grabbing;
  }
`;

const DurationOptionsContainer = styled.div`
  display: flex;
  margin: 35px 10px 15px;
  gap: 12px;
`;

const DurationOption = styled.button<{ selected?: boolean }>`
  flex: 1;
  padding: 15px 0;
  border-radius: 12px;
  border: 2px solid
    ${(props) => (props.selected ? '#7354F2' : 'rgba(180, 180, 180, 0.2)')};
  background: none;
  color: #ffffff;
  font-size: 13px;
  font-family: 'Inter', sans-serif;
`;

const StakeButton = styled.button<{ disabled: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  padding: 20px;
  margin: 0 11px;
  border-radius: 12px;
  border: 2px solid
    ${(props) =>
      props.disabled ? 'rgba(180, 180, 180, 0.1)' : 'rgba(180, 180, 180, 0.2)'};
  width: calc(100% - 22px);
  color: ${(props) => (props.disabled ? '#666666' : '#FFFFFF')};
  font-size: 13px;
  font-weight: bold;
  font-family: 'Manrope', sans-serif;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

const MonthsText = styled.span`
  color: #666666;
  font-size: 12px;
`;

const modalVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalContentVariants = {
  hidden: { y: '100%' },
  visible: { y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
  exit: { y: '100%', transition: { duration: 0.3, ease: 'easeIn' } },
};

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: #151515;
  width: 100%;
  max-width: 380px;
  border-radius: 20px;
  padding: 30px 20px;
  margin: 10px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalTitle = styled.h3`
  font-family: 'Manrope', sans-serif;
  font-size: 28px;
  color: #ffffff;
  margin: 0 0 16px 0;
  text-align: center;
`;

const ModalText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #ffffff;
  text-align: center;
  line-height: 145%;
  margin: 0 0 24px 0;
`;

const HomeButtonIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const HomeButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  background-color: transparent;
`;

interface FivePractica1Props {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onNavigateToPractica2?: () => void; // Add this prop
  toHome?: () => void;
}

const LANG_KEY = 'stonpools-lang';

const FivePractica1: React.FC<FivePractica1Props> = ({
  onSwipeLeft,
  onSwipeRight,
  onNavigateToPractica2,
  toHome,
}) => {
  const [amount, setAmount] = useState<string>(''); // Changed from '0' to empty string
  const [duration, setDuration] = useState<number>(24);
  const [isDragging, setIsDragging] = useState(false);
  const [currentPosition, setCurrentPosition] = useState<number>(100);
  const [showModal, setShowModal] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const lang = (localStorage.getItem(LANG_KEY) as 'ru' | 'en') || 'ru';
  const t = fivePracticaLocales[lang]?.[1] || {};

  const getSnapPosition = (
    position: number
  ): { pos: number; months: number } => {
    if (position < 16.67) return { pos: 0, months: 3 };
    if (position < 50) return { pos: 33.33, months: 6 };
    if (position < 83.33) return { pos: 66.66, months: 12 };
    return { pos: 100, months: 24 };
  };

  const handleSliderMove = (clientX: number) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const position = ((clientX - rect.left) / rect.width) * 100;
    const clampedPosition = Math.max(0, Math.min(100, position));

    const snap = getSnapPosition(clampedPosition);
    setCurrentPosition(snap.pos);
    setDuration(snap.months);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    handleSliderMove(e.clientX);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    handleSliderMove(e.clientX);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    handleSliderMove(e.touches[0].clientX);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging) return;
    handleSliderMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleMouseUp();
  };

  const handleSetDuration = (months: number) => {
    const snapPoint = getSnapPosition(
      months === 3 ? 0 : months === 6 ? 33.33 : months === 12 ? 66.66 : 100
    );
    setDuration(months);
    setCurrentPosition(snapPoint.pos);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    const cleanValue = value.replace(/^0+(?=\d)/, '');
    if (Number(cleanValue) > 1000) {
      setShowModal(true);
      setAmount('1000');
      inputRef.current?.blur();
    } else {
      setAmount(cleanValue);
    }
  };

  const handleStakeClick = () => {
    if (Number(amount) > 0 && typeof onNavigateToPractica2 === 'function') {
      onNavigateToPractica2();
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => onSwipeLeft?.(),
    onSwipedRight: () => onSwipeRight?.(),
    trackMouse: true,
    delta: 50,
    swipeDuration: 500,
  });

  return (
    <div {...handlers}>
      <GlobalFonts />
      <Container>
        <ScrollContainer>
          <HomeButton onClick={toHome}>
            <HomeButtonIcon src="/assets/homeIconPurple.svg" />
          </HomeButton>
          <Header>
            <Title>{t.title}</Title>
            <TheoryButton>{t.practica}</TheoryButton>
          </Header>
          <Description>{t.description}</Description>
          <StakingContainer>
            <Label>{t.amountLabel}</Label>
            <AmountButton>
              <AmountInput
                ref={inputRef}
                type="text"
                value={amount}
                onChange={handleAmountChange}
                placeholder="0"
              />
              <CurrencyContainer>
                <CurrencyIcon src="/assets/ston.svg" />
                <CurrencyText>STON</CurrencyText>
              </CurrencyContainer>
            </AmountButton>
            <Label>{t.durationLabel}</Label>
            <DurationButton>
              <AmountText>{duration}</AmountText>
              <MonthsText>{t.months}</MonthsText>
              <DurationSliderContainer
                ref={sliderRef}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
              >
                <SliderTrack />
                <SliderProgress
                  progress={currentPosition}
                  isDragging={isDragging}
                />
                <SliderThumb
                  position={currentPosition}
                  isDragging={isDragging}
                />
              </DurationSliderContainer>
            </DurationButton>
            <DurationOptionsContainer>
              <DurationOption
                selected={duration === 3}
                onClick={() => handleSetDuration(3)}
              >
                3{t.mShort}
              </DurationOption>
              <DurationOption
                selected={duration === 6}
                onClick={() => handleSetDuration(6)}
              >
                6{t.mShort}
              </DurationOption>
              <DurationOption
                selected={duration === 12}
                onClick={() => handleSetDuration(12)}
              >
                12{t.mShort}
              </DurationOption>
              <DurationOption
                selected={duration === 24}
                onClick={() => handleSetDuration(24)}
              >
                24{t.mShort}
              </DurationOption>
            </DurationOptionsContainer>
            <StakeButton
              disabled={Number(amount) <= 0}
              onClick={handleStakeClick}
            >
              {t.stakeButton}
            </StakeButton>
          </StakingContainer>
          <Footer
            practica
            textLeft="назад"
            onSwipeLeft={onSwipeLeft}
            onSwipeRight={onSwipeRight}
          />
          <AnimatePresence>
            {showModal && (
              <ModalOverlay
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
                onClick={() => setShowModal(false)}
              >
                <ModalContent
                  onClick={(e) => e.stopPropagation()}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={modalContentVariants}
                >
                  <ModalTitle>{t.modalTitle}</ModalTitle>
                  <ModalText>{t.modalText}</ModalText>
                  <StakeButton
                    disabled={false}
                    onClick={() => setShowModal(false)}
                  >
                    {t.modalButton}
                  </StakeButton>
                </ModalContent>
              </ModalOverlay>
            )}
          </AnimatePresence>
        </ScrollContainer>
      </Container>
    </div>
  );
};

export default FivePractica1;
